import { Component, signal, computed, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { FhirApiService, FhirMapperService, RdaStateService } from '../../core/services';
import { FHIRBundleEntry, ConsultarRdaCompletoResponse } from '../../shared/models/fhir.models';
import { decryptUrlPayload } from '../../core/services/url-crypto.util';
import { environment } from '../../../environments/environment';
import {
  PatientCardComponent,
  ImmunizationTableComponent,
  CompositionTableComponent,
  DetailModalComponent,
} from '../../shared/components/organisms';

@Component({
  selector: 'app-visor-rda',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PatientCardComponent,
    ImmunizationTableComponent,
    CompositionTableComponent,
    DetailModalComponent,
  ],
  templateUrl: './visor-rda.component.html',
  styleUrl: './visor-rda.component.scss',
})
export class VisorRdaComponent implements OnInit {
  private readonly fhirApi = inject(FhirApiService);
  private readonly fhirMapper = inject(FhirMapperService);
  readonly state = inject(RdaStateService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  // ── Debug logger ─────────────────────────────────────────────────────────
  private readonly logger = { debug: (msg: string) => console.debug(msg) };

  // ── Form ───────────────────────────────────────────────────────────────────
  protected readonly tipoDocumento = signal('CC');
  protected readonly numeroDocumento = signal('');
  protected readonly fechaDesde = signal(new Date().toISOString().slice(0, 10));
  protected readonly activarFiltroFecha = signal(false);
  protected readonly successMessage = signal<string | null>(null);

  /**
   * humanUser (the user performing the query, for IHCE audit purposes).
   * May arrive from an external app via the `humanUser` query param.
   * If not provided, it is derived from `${tipoDoc}-${documento}` (previous behavior).
   */
  protected readonly humanUserOverride = signal<string | null>(null);

  /**
   * El visor siempre se abre desde una app externa (ej. FoxPro) con la consulta
   * precargada, por lo que el formulario de búsqueda manual siempre está oculto:
   * el usuario solo ve los resultados.
   */
  protected readonly hiddenForm = signal(true);

  // ── Modal ────────────────────────────────────────────────────────────────
  protected readonly selectedComposition = signal<FHIRBundleEntry | null>(null);
  protected readonly showModal = signal(false);

  // ── Accordions ───────────────────────────────────────────────────────────
  protected readonly accordionStates = signal({
    patient: false,
    immunizations: false,
    encounters: false,
    history: false,
  });

  // ── Computed: separated entries (delegated to state) ─────────────────────
  readonly documentosHistorial = computed(() => this.state.documentosHistorial());
  readonly documentosEncuentros = computed(() => this.state.documentosEncuentros());

  /** True when there is at least one page in the navigation history */
  readonly canGoBack = computed(() => this.state.pageHistory().length > 0);

  // ── Bootstrap: input parameters from external app ────────────────────────

  /**
   * Allows opening the viewer from an external app (e.g. VBA) with the query
   * already preloaded. Two input modes are supported:
   *
   * 1. Encrypted (recommended for PII) — a single `d` param carrying the whole
   *    payload encrypted with AES-256-GCM (shared key in environment.urlCryptoKey):
   *
   *      /#/visor?d=<base64url(IV || ciphertext || tag)>
   *
   *    Decrypted plaintext is a JSON object:
   *      { "identifierType": "CC", "identifierValue": "123", "humanUser": "CC-...",
   *        "fechaDesde": "2026-01-01" }
   *
   * 2. Plaintext (dev/testing only) — the individual params after the `#`:
   *
   *      /#/visor?identifierType=CC&identifierValue=1234567890&humanUser=CC-88208800
   *
   *    Optional `fechaDesde` (YYYY-MM-DD) enables the date filter.
   *
   * Behavior:
   *  1. Hydrates the form with the received parameters.
   *  2. If `identifierValue` is present, triggers the query automatically.
   *  3. Clears the URL query params (replaceUrl) so nothing sensitive is left
   *     in the address bar or the history.
   *
   * Note: the tenant credential (X-API-Key) does NOT travel via URL; it is
   * resolved by TenantApiKeyInterceptor from the hostname.
   */
  async ngOnInit(): Promise<void> {
    const qp = this.route.snapshot.queryParamMap;
    const encrypted = qp.get('d');

    let tipo: string | null;
    let doc: string | null;
    let human: string | null;
    let fecha: string | null;

    if (encrypted) {
      // Encrypted mode: decrypt the `d` payload into the individual params.
      try {
        const json = await decryptUrlPayload(encrypted, environment.urlCryptoKey);
        const payload = JSON.parse(json) as {
          identifierType?: string;
          identifierValue?: string;
          humanUser?: string;
          fechaDesde?: string;
        };
        tipo = payload.identifierType ?? null;
        doc = payload.identifierValue ?? null;
        human = payload.humanUser ?? null;
        fecha = payload.fechaDesde ?? null;
      } catch {
        this.state.error.set('El enlace recibido no es válido o está corrupto');
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {},
          replaceUrl: true,
        });
        return;
      }
    } else {
      // Plaintext mode (dev/testing).
      tipo = qp.get('identifierType');
      doc = qp.get('identifierValue');
      human = qp.get('humanUser');
      fecha = qp.get('fechaDesde');
    }

    if (tipo) this.tipoDocumento.set(tipo);
    if (doc) this.numeroDocumento.set(doc.trim());
    if (human) this.humanUserOverride.set(human);

    // Optional date filter (YYYY-MM-DD format). Enables the filter and feeds
    // both lastUpdatedStart and authoredOnStart in the query.
    if (fecha) {
      this.fechaDesde.set(fecha.trim());
      this.activarFiltroFecha.set(true);
    }

    const tieneParametros = !!(encrypted || tipo || doc || human || fecha);

    // Auto-run: only when the document arrives (the minimum required datum).
    if (doc?.trim()) {
      this.consultarInformacion();
    }

    // Clear the URL after reading the parameters (avoids persistent PII).
    if (tieneParametros) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {},
        replaceUrl: true,
      });
    }
  }

  // ── Main query ─────────────────────────────────────────────────────────────

  /**
   * Fires a single call to the consultar-rda-completo endpoint which:
   *  1. Queries both RDA sources in parallel on the backend.
   *  2. Resolves all referenced FHIR resources.
   *  3. Returns the combined Bundle + resources + summary.
   *
   * The response is wrapped by the backend's TransformInterceptor:
   *   { success, data: ConsultarRdaCompletoResponse, timestamp }
   * The frontend's ApiResponseInterceptor unwraps the `data` level automatically.
   *
   * Additionally queries immunizations in parallel (non-blocking if it fails).
   */
  consultarInformacion(): void {
    const documento = this.numeroDocumento().trim();
    const tipoDoc = this.tipoDocumento();

    if (!documento) {
      this.state.error.set('Por favor ingrese un número de documento');
      return;
    }

    this.state.loading.set(true);
    this.state.error.set(null);
    this.successMessage.set(null);
    this.state.reset();

    const humanUser = this.humanUserOverride() ?? `${tipoDoc}-${documento}`;

    const rdaRequest = {
      identifierType: tipoDoc,
      identifierValue: documento,
      humanUser,
      ...(this.activarFiltroFecha() && {
        lastUpdatedStart: this.fechaDesde(),
        authoredOnStart: this.fechaDesde(),
      }),
    };

    const inmunizacionRequest = {
      identifierType: tipoDoc,
      identifierValue: documento,
      humanUser,
    };

    // Full RDA query + immunizations in parallel
    // Immunizations do not block if they fail
    this.fhirApi.consultarRdaCompleto(rdaRequest).subscribe({
      next: (rdaCompleto: ConsultarRdaCompletoResponse) => {
        // ── Store the unified response in the state ─────────────────────
        this.state.rdaCompletoResponse.set(rdaCompleto);

        // ── Save a snapshot of the first page ───────────────────────────
        // Used to restore the state when the user navigates back
        // to the initial page (without re-querying the backend).
        this.state.initialRdaResponse.set(rdaCompleto);

        // ── Record the first page URL (for history) ─────────────────────
        // If the server returns a `self` link, we use it as the base URL.
        // Otherwise, we use a special marker meaning "initial page".
        const selfLink = rdaCompleto.link?.find((l) => l.relation === 'self');
        this.state.currentPageUrl.set(selfLink?.url ?? '__initial__');

        // ── Extract and map the patient data ────────────────────────────
        const patient = rdaCompleto.referencedResources?.patients?.[0];
        if (patient) {
          this.state.patientData.set(this.fhirMapper.mapPatient(patient));
          this.state.currentPatientId.set(patient.id ?? '');
        }

        // ── Open all accordions ─────────────────────────────────────────
        this.accordionStates.set({
          patient: true,
          immunizations: true,
          encounters: true,
          history: true,
        });

        this.state.loading.set(false);
        this.successMessage.set('Búsqueda efectuada de manera correcta');
        setTimeout(() => this.successMessage.set(null), 5000);

        // ── Query immunizations independently ───────────────────────────
        this.state.loadingImm.set(true);
        this.fhirApi
          .consultarInmunizacion(inmunizacionRequest)
          .pipe(catchError(() => of(null)))
          .subscribe((inmunizacion) => {
            if (inmunizacion?.raw) {
              this.state.immunizations.set(this.fhirMapper.extractImmunizations(inmunizacion.raw));
            }
            this.state.loadingImm.set(false);
          });
      },
      error: (err: any) => {
        console.error('Error en consulta RDA completa:', err);
        this.state.error.set(err.error?.message ?? err.message ?? 'Error al consultar información');
        this.state.loading.set(false);
      },
    });
  }

  // ── Pagination ─────────────────────────────────────────────────────────────

  /**
   * Navigates to a results page using the IHCE pagination link.
   *
   * The backend:
   *  1. Proxies the GET to IHCE with authentication.
   *  2. Resolves the FHIR references of the new page.
   *  3. Accumulates the resources (de-duplicated) with those of previous pages,
   *     using patientId as the cache key.
   *
   * The response includes: Bundle + accumulated referencedResources + counters.
   */
  cargarPagina(url: string): void {
    if (!url || this.state.loading()) return;

    // ── Push the current URL before navigating ─────────────────────────────
    // We use currentPageUrl (the URL loaded last time), not the server's
    // `self` link — IHCE does not always include it on every page.
    const currentUrl = this.state.currentPageUrl();
    if (currentUrl) {
      this.state.pageHistory.update((history) => [...history, currentUrl]);
    }

    // Record the URL we are navigating to as the new current page
    this.state.currentPageUrl.set(url);

    this.cargarPaginaInternal(url);
  }

  /**
   * Goes back to the previous page using the client-side navigation history.
   * Pops the last pushed URL and navigates to it.
   * This works even when the IHCE server does not return a `prev` link.
   */
  goBack(): void {
    const history = this.state.pageHistory();
    if (!history.length || this.state.loading()) return;

    const previousUrl = history[history.length - 1];

    // Pop from history
    this.state.pageHistory.update((h) => h.slice(0, -1));

    // Update the current URL to the one we are returning to
    this.state.currentPageUrl.set(previousUrl);

    // '__initial__' is the marker for the first page.
    // Instead of making a new HTTP request, we restore the snapshot
    // saved in initialRdaResponse — instant and with no network cost.
    if (previousUrl === '__initial__') {
      const initial = this.state.initialRdaResponse();
      if (initial) {
        this.state.rdaCompletoResponse.set(initial);
      }
      return;
    }

    // For any other page, navigate without pushing again
    this.cargarPaginaInternal(previousUrl);
  }

  /**
   * Internal version of cargarPagina that does NOT push to history.
   * Used by goBack() to avoid duplicating entries when going back.
   */
  private cargarPaginaInternal(url: string): void {
    if (!url || this.state.loading()) return;

    const patientId = this.state.currentPatientId() || undefined;

    this.state.loading.set(true);
    this.state.error.set(null);

    this.fhirApi.navegarPagina(url, patientId).subscribe({
      next: (response: any) => {
        if (!response || response.resourceType !== 'Bundle') {
          this.state.loading.set(false);
          return;
        }

        const entries: any[] = (response.entry ?? []).map((e: any) => ({
          ...e,
          _source: 'rda-paciente',
          _sourceLabel: 'RDA de antecedentes manifestados por el paciente',
        }));

        const current = this.state.rdaCompletoResponse();

        this.state.rdaCompletoResponse.set({
          resourceType: 'Bundle',
          type: 'searchset',
          total: response.total ?? entries.length,
          entry: entries,
          link: response.link ?? [],
          entriesBySource: {
            paciente: entries,
            encuentros: current?.entriesBySource?.encuentros ?? [],
          },
          rdaDetails: current?.rdaDetails ?? {
            paciente: { status: 'fulfilled', total: 0, entries: 0, error: null },
            encuentros: { status: 'fulfilled', total: 0, entries: 0, error: null },
          },
          referencedResources:
            response.referencedResources ??
            current?.referencedResources ??
            this.emptyReferencedResources(),
          summary: current?.summary ?? this.emptySummary(),
        });

        this.state.loading.set(false);
      },
      error: (err: any) => {
        console.error('Error cargando página (goBack):', err);
        this.state.error.set(err.error?.message ?? err.message ?? 'Error al cargar página');
        this.state.loading.set(false);
      },
    });
  }

  getPaginationLabel(relation: string): string {
    const labels: Record<string, string> = {
      next: '▶ Siguiente',
      prev: '◀ Anterior',
      previous: '◀ Anterior',
      first: '⏮ Primera',
      last: '⏭ Última',
      self: '📍 Actual',
    };
    return labels[relation] || relation;
  }

  getPaginationButtonClass(relation: string): string {
    switch (relation) {
      case 'next':
        return 'btn btn-primary btn-sm';
      case 'prev':
      case 'previous':
        return 'btn btn-outline-secondary btn-sm';
      case 'first':
        return 'btn btn-outline-dark btn-sm';
      case 'last':
        return 'btn btn-outline-dark btn-sm';
      default:
        return 'btn btn-outline-primary btn-sm';
    }
  }

  // ── Form ───────────────────────────────────────────────────────────────────

  limpiarFormulario(): void {
    this.numeroDocumento.set('');
    this.tipoDocumento.set('CC');
    this.fechaDesde.set(new Date().toISOString().slice(0, 10));
    this.activarFiltroFecha.set(false);
    this.selectedComposition.set(null);
    this.showModal.set(false);
    this.state.reset();
    this.accordionStates.set({
      patient: false,
      immunizations: false,
      encounters: false,
      history: false,
    });
  }

  // ── Accordion ────────────────────────────────────────────────────────────

  toggleAccordion(section: 'patient' | 'immunizations' | 'encounters' | 'history'): void {
    const scrollPosition = { x: window.scrollX, y: window.scrollY };
    const documentHeight = document.documentElement.scrollHeight;

    const current = this.accordionStates();
    this.accordionStates.set({ ...current, [section]: !current[section] });

    window.scrollTo(scrollPosition.x, scrollPosition.y);
    requestAnimationFrame(() => {
      window.scrollTo(scrollPosition.x, scrollPosition.y);
      setTimeout(() => {
        const newHeight = document.documentElement.scrollHeight;
        if (Math.abs(newHeight - documentHeight) < 1000) {
          window.scrollTo(scrollPosition.x, scrollPosition.y);
        }
      }, 100);
    });
  }

  // ── Modal ────────────────────────────────────────────────────────────────

  mostrarDetalle(entry: FHIRBundleEntry): void {
    this.selectedComposition.set(entry);
    this.showModal.set(true);
  }

  cerrarModal(): void {
    this.showModal.set(false);
    this.selectedComposition.set(null);
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  private emptyReferencedResources() {
    return {
      patients: [],
      encounters: [],
      practitioners: [],
      practitionerRoles: [],
      organizations: [],
      locations: [],
      conditions: [],
      allergyIntolerances: [],
      medicationStatements: [],
      medicationAdministrations: [],
      medicationRequests: [],
      familyMemberHistories: [],
      procedures: [],
      observations: [],
      riskAssessments: [],
      serviceRequests: [],
      documentReferences: [],
    };
  }

  private emptySummary() {
    return {
      totalCompositions: 0,
      compositionsPaciente: 0,
      compositionsEncuentros: 0,
      patients: 0,
      encounters: 0,
      practitioners: 0,
      organizations: 0,
      conditions: 0,
      allergies: 0,
      medications: 0,
      familyHistory: 0,
      procedures: 0,
      observations: 0,
      locations: 0,
      riskAssessments: 0,
      serviceRequests: 0,
      documentReferences: 0,
    };
  }
}
